import { Stack } from "@mui/material";
import {
  CopyLinkButton,
  MANUAL_REVIEW_LINK_TARGET,
} from "components/labelling/CopyLinkButton";
import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { TimelineZoomProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import { PendingAreaSelection } from "components/scrapEyeLabelling/hooks/usePendingAreaSelection";
import { AreaMaterialMappingModal } from "components/scrapEyeLabelling/modals/AreaMaterialMappingModal";
import { AreaSelectionModal } from "components/scrapEyeLabelling/modals/AreaSelectionModal";
import { AreaMaterialSnapshot } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { useCallback, useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { LabellerTimeline, ReviewPeriodData } from "src/store/api/generatedApi";
import { useManualReviewEvents } from "./contexts/ManualReviewEventContext";
import { MANUAL_REVIEW_SCOPE, SUB_MODAL_HOTKEY_SCOPES } from "./hotkeyScopes";
import { useManualReviewActions } from "./hooks/useManualReviewActions";
import { ManualReviewActionButtons } from "./ManualReviewActionButtons";
import { ManualReviewVideoPlayer } from "./ManualReviewVideoPlayer";
import { VideoSuspensionProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoSuspensionContext";

interface ManualReviewPlayerSectionProps {
  location: string;
  footageLocation?: string;
  reviewPeriods: ReviewPeriodData[];
  displayedReviewPeriods?: ReviewPeriodData[];
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  debouncedWindowStart: Dayjs;
  debouncedWindowEnd: Dayjs;
  displayStart?: Dayjs;
  displayEnd?: Dayjs;
  isEventsFetching: boolean;
  allReviewPeriods: ReviewPeriodData[];
  isVerifying: boolean;
  verifyPeriods: (
    periods: ReviewPeriodData[],
    allEvents: ScrapEyesEventWithReviewData[],
    originalEvents: ScrapEyesEventWithReviewData[],
    options?: {
      includeVerified?: boolean;
    }
  ) => Promise<void>;
  goToPeriod: (period: ReviewPeriodData) => void;
  verifyPeriodStarts?: string[];
  labellerTimelines: LabellerTimeline[];
  highlightColorsByEventId?: ReadonlyMap<string, string | null>;

  // Hotkey-related props passed from parent
  pendingAreaSelection: PendingAreaSelection | null;
  clearPendingAreaSelection: () => void;
  handleAreaSelect: (area: string) => void;
  isAreaMaterialMappingOpen: boolean;
  closeAreaMaterialMappingModal: () => void;
  editingSnapshot: AreaMaterialSnapshot | null;
  currentAreaMaterialMapping: Record<string, string>;
  reportedMaterialsAtMappingTime: Record<string, string>;
  miniMapAreaColors: Record<string, string>;
  handleAreaMaterialUpdate: (area: string, material: string | null) => void;
  handleSnapshotMappingUpdate: (area: string, material: string | null) => void;
  handleDeleteSnapshot: (snapshot: AreaMaterialSnapshot) => void;
  handleMappingSnapshotClick: (snapshot: AreaMaterialSnapshot) => void;
  editingSnapshotCurrentMapping: Record<string, string>;

  isLoading?: boolean;

  onVerifyActionReady?: (verifyAction: (() => Promise<void>) | null) => void;
}

export const ManualReviewPlayerSection = ({
  location,
  footageLocation,
  reviewPeriods,
  displayedReviewPeriods,
  viewingWindowStart,
  viewingWindowEnd,
  debouncedWindowStart,
  debouncedWindowEnd,
  displayStart,
  displayEnd,
  isEventsFetching,
  allReviewPeriods,
  isVerifying,
  verifyPeriods,
  goToPeriod,
  verifyPeriodStarts,
  labellerTimelines,
  highlightColorsByEventId,
  pendingAreaSelection,
  clearPendingAreaSelection,
  handleAreaSelect,
  isAreaMaterialMappingOpen,
  closeAreaMaterialMappingModal,
  editingSnapshot,
  reportedMaterialsAtMappingTime,
  currentAreaMaterialMapping,
  miniMapAreaColors,
  handleAreaMaterialUpdate,
  handleSnapshotMappingUpdate,
  handleDeleteSnapshot,
  handleMappingSnapshotClick,
  editingSnapshotCurrentMapping,
  isLoading,
  onVerifyActionReady,
}: ManualReviewPlayerSectionProps) => {
  const start = displayStart ?? viewingWindowStart;
  const end = displayEnd ?? viewingWindowEnd;
  const duration = end.diff(start, "second");
  const { areaMaterialSnapshots } = useManualReviewEvents();

  const {
    verifyWindowAndGoToNext,
    handleUnverifyWindow,
    hasVerifiedPeriods,
    hasEdits,
    isEventsInSync,
    isEditMode,
    enterEditMode,
    cancelEditMode,
    submitEdits,
    isBringInChainRunning,
  } = useManualReviewActions({
    location,
    reviewPeriods,
    viewingWindowStart,
    viewingWindowEnd,
    debouncedWindowStart,
    debouncedWindowEnd,
    isEventsFetching,
    allReviewPeriods,
    verifyPeriods,
    goToPeriod,
    verifyPeriodStarts,
  });

  // The parent's hotkey registration outlives this section's mount.
  const activeAction = isEditMode ? submitEdits : verifyWindowAndGoToNext;
  useEffect(() => {
    onVerifyActionReady?.(activeAction);
    return () => {
      onVerifyActionReady?.(null);
    };
  }, [activeAction, onVerifyActionReady]);

  const { getHotkey } = useHotkeyContext();
  const toggleEditMode = useCallback(() => {
    if (isEditMode) {
      void cancelEditMode();
    } else {
      enterEditMode();
    }
  }, [isEditMode, cancelEditMode, enterEditMode]);
  useHotkeys(
    getHotkey("editWindow"),
    toggleEditMode,
    {
      enableOnFormTags: false,
      scopes: [MANUAL_REVIEW_SCOPE],
      enabled: hasVerifiedPeriods,
    },
    [toggleEditMode]
  );

  return (
    <TimelineZoomProvider fullDuration={duration} videoStart={start}>
      <VideoSuspensionProvider suspended={isBringInChainRunning}>
        <ManualReviewVideoPlayer
          location={location}
          footageLocation={footageLocation}
          start={start}
          end={end}
          reviewPeriods={displayedReviewPeriods ?? reviewPeriods}
          isLoading={isLoading}
          isEditMode={isEditMode}
          labellerTimelines={labellerTimelines}
          areaMaterialSnapshots={areaMaterialSnapshots}
          onMappingSnapshotClick={handleMappingSnapshotClick}
          selectedSnapshotTimestamp={editingSnapshot?.timestamp.unix() ?? null}
          areaColors={miniMapAreaColors}
          highlightColorsByEventId={highlightColorsByEventId}
          actionButtons={
            <Stack direction="row" spacing={1} alignItems="center">
              <CopyLinkButton
                location={location}
                videoStart={start}
                linkTarget={MANUAL_REVIEW_LINK_TARGET}
              />
              <ManualReviewActionButtons
                hasVerifiedPeriods={hasVerifiedPeriods}
                hasEdits={hasEdits}
                isVerifying={isVerifying}
                isEventsInSync={isEventsInSync}
                isEditMode={isEditMode}
                onUnverifyWindow={() => void handleUnverifyWindow()}
                onVerifyWindow={() => void verifyWindowAndGoToNext()}
                onEnterEditMode={enterEditMode}
                onSubmitEdits={() => void submitEdits()}
                onCancelEditMode={() => void cancelEditMode()}
              />
            </Stack>
          }
        />
      </VideoSuspensionProvider>

      <AreaSelectionModal
        open={pendingAreaSelection !== null}
        onClose={clearPendingAreaSelection}
        onSelectArea={handleAreaSelect}
        eventType={pendingAreaSelection?.eventType ?? "lift"}
        areaColors={miniMapAreaColors}
        disableScopes={SUB_MODAL_HOTKEY_SCOPES}
        openedOnKeyRelease={pendingAreaSelection?.openedOnKeyRelease ?? false}
        location={location}
      />

      <AreaMaterialMappingModal
        open={isAreaMaterialMappingOpen || editingSnapshot !== null}
        onClose={closeAreaMaterialMappingModal}
        currentMapping={
          editingSnapshot
            ? editingSnapshotCurrentMapping
            : currentAreaMaterialMapping
        }
        reportedMaterials={reportedMaterialsAtMappingTime}
        isLoadingReportedMaterials={isLoading}
        onUpdateMapping={
          editingSnapshot
            ? handleSnapshotMappingUpdate
            : handleAreaMaterialUpdate
        }
        snapshot={editingSnapshot ?? undefined}
        onDeleteSnapshot={handleDeleteSnapshot}
        disableScopes={SUB_MODAL_HOTKEY_SCOPES}
        location={location}
      />
    </TimelineZoomProvider>
  );
};
